import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Divider, Heading, Modal, ProgressStepper } from '@zillow/constellation-10';

import ExitPaymentSetup from '../ExitPaymentSetup/component';
import WizardShellToasts from './WizardShellToasts';
import { WizardShellContext } from './context';
import { getWizardScreen, WIZARD_CATEGORIES } from './screens';
import type { WizardCategory } from './screens';

const CONTENT_MAX_WIDTH_WITH_ASIDE = '728px';
const CONTENT_MAX_WIDTH_WITHOUT_ASIDE = '720px';
const ASIDE_WIDTH = '328px';
const TITLE_ID = 'payment-setup-wizard-title';

const gutters = { base: 'device.margin.mobile', md: 'device.margin.tablet', xl: 'device.margin.desktop' } as const;
const container = { width: '100%', maxWidth: 'breakpoint-xxl', marginInline: 'auto', paddingInline: gutters } as const;

type Props = {
  /** Current route pathname; drives the stepper and per-step focus/scroll reset. */
  activePathname: string;
  /** Categories the current flow skips entirely (e.g. Lease when the property has no leases). */
  hiddenCategories?: Array<WizardCategory>;
  /** Called after the user confirms in the exit dialog. Omit to hide the Exit button. */
  onSaveAndExit?: () => void;
  children: ReactNode;
};

const WizardShell = ({ activePathname, hiddenCategories, onSaveAndExit, children }: Props): JSX.Element => {
  const [footer, setFooter] = useState<ReactNode>(null);
  const [aside, setAside] = useState<ReactNode>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const contextValue = useMemo(() => ({ setFooter, setAside }), []);
  const currentScreen = getWizardScreen(activePathname);

  const visibleCategories = WIZARD_CATEGORIES.filter((category) => !hiddenCategories?.includes(category));
  const activeStepIndex = currentScreen?.category ? visibleCategories.indexOf(currentScreen.category) : 0;
  const showStepper = Boolean(currentScreen?.showStepper) && activeStepIndex !== -1;

  const steps = visibleCategories.map((label, index) => ({
    label,
    tone:
      currentScreen?.category && activeStepIndex > index
        ? ('complete' as const)
        : currentScreen?.category && activeStepIndex === index
          ? ('incomplete' as const)
          : undefined,
  }));

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
      bodyRef.current.focus({ preventScroll: true });
    }
  }, [activePathname]);

  return (
    <WizardShellContext.Provider value={contextValue}>
      <Modal.Root
        size="fullScreen"
        open
        onOpenChange={() => {
          /* The wizard never dismisses itself; exiting goes through the Exit confirm. */
        }}
        useDismissProps={{ escapeKey: false, outsidePress: false }}
      >
        <Modal.Portal>
          <Modal.Backdrop>
            <Modal.Content aria-labelledby={TITLE_ID}>
              <Modal.Header
                css={{
                  ...container,
                  paddingInlineStart: gutters,
                  paddingInlineEnd: gutters,
                  paddingBlock: { base: 'layout.tight', md: 'layout.default' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'layout.tight',
                  justifyContent: 'space-between',
                }}
              >
                <Heading id={TITLE_ID} level={1}>
                  Payment setup
                </Heading>
                {onSaveAndExit && (
                  <Button
                    tone="neutral"
                    emphasis="outlined"
                    css={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    onClick={() => setShowExitConfirm(true)}
                  >
                    Exit
                  </Button>
                )}
              </Modal.Header>
              <Divider />
              <Modal.Body css={{ paddingInline: 'none', paddingBlock: 'layout.default' }}>
                <Box css={container}>
                  <WizardShellToasts />
                  {showStepper && (
                    <ProgressStepper
                      css={{ marginBlockEnd: { base: 'layout.loose', md: 'layout.looser' } }}
                      activeStepIndex={activeStepIndex}
                      steps={steps}
                    />
                  )}
                  <Box
                    css={{
                      display: 'grid',
                      gridTemplateColumns: {
                        base: 'minmax(0, 1fr)',
                        xl: aside
                          ? `minmax(0, ${CONTENT_MAX_WIDTH_WITH_ASIDE}) ${ASIDE_WIDTH}`
                          : `minmax(0, ${CONTENT_MAX_WIDTH_WITHOUT_ASIDE})`,
                      },
                      columnGap: 'layout.vast.looser',
                      rowGap: { base: 'layout.loose', md: 'layout.looser' },
                    }}
                  >
                    <Box ref={bodyRef} tabIndex={-1}>
                      {children}
                    </Box>
                    {aside && <Box css={{ alignSelf: 'start' }}>{aside}</Box>}
                  </Box>
                </Box>
              </Modal.Body>
              {footer && (
                <>
                  <Divider />
                  <Modal.Footer
                    css={{
                      paddingInline: 'none',
                      paddingBlock: { base: 'layout.tight', md: 'layout.default' },
                    }}
                  >
                    <Box css={container}>{footer}</Box>
                  </Modal.Footer>
                </>
              )}
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
      {showExitConfirm && onSaveAndExit && (
        <ExitPaymentSetup
          exitPaymentSetup={() => {
            setShowExitConfirm(false);
            onSaveAndExit();
          }}
          onBack={() => setShowExitConfirm(false)}
        />
      )}
    </WizardShellContext.Provider>
  );
};

export default WizardShell;
